import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, Save, Image as ImageIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { HomepageSettingsService, CollectionsService } from '../firebase';
import { HomepageSettings } from '../types/collection';

const Container = styled.div`
  padding: 20px;
`;

const Section = styled.div`
  background: white;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h3`
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  margin-right: 10px;
  margin-bottom: 10px;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return 'background: #1a1a1a; color: white;';
      case 'danger':
        return 'background: #dc3545; color: white;';
      default:
        return 'background: #f8f9fa; color: #333; border: 1px solid #e1e5e9;';
    }
  }}

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ImageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const ImageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e1e5e9;
`;

const ImagePreview = styled.div<{ $bgImage: string }>`
  width: 150px;
  height: 100px;
  background: url(${props => props.$bgImage}) center/cover;
  border-radius: 8px;
  border: 2px solid #e1e5e9;
  flex-shrink: 0;
`;

const ImageUrl = styled.div`
  flex: 1;
  font-size: 14px;
  color: #666;
  word-break: break-all;
  font-family: monospace;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

const AddImageContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const Message = styled.div<{ $type?: 'success' | 'error' }>`
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'error' ? '#fee' : '#efe'};
  color: ${props => props.$type === 'error' ? '#c33' : '#3c3'};
  border: 1px solid ${props => props.$type === 'error' ? '#fcc' : '#cfc'};
`;

const CollectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const CollectionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e1e5e9;
`;

const CollectionInfo = styled.div`
  flex: 1;
`;

const CollectionTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
`;

const CollectionDescription = styled.div`
  font-size: 14px;
  color: #666;
`;

const DescriptionText = styled.p`
  color: #666;
  margin-bottom: 20px;
`;

const CheckboxInput = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const EmptyMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #999;
`;

const HomepageManagement: React.FC = () => {
  const [settings, setSettings] = useState<HomepageSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  
  // Hero Section
  const [newHeroImageUrl, setNewHeroImageUrl] = useState('');
  
  // Collections
  const [availableCollections, setAvailableCollections] = useState<any[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);

  useEffect(() => {
    loadSettings();
    loadCollections();
    // İlk açılışta eğer ayarlar yoksa Google Drive linkini ekle
    initializeIfNeeded();
  }, []);

  const initializeIfNeeded = async () => {
    try {
      const result = await HomepageSettingsService.getHomepageSettings();
      // Eğer ayarlar yoksa veya heroSlides boşsa, Google Drive linkini ekle
      if (result.success && (!result.data?.heroSlides || result.data.heroSlides.length === 0)) {
        // Google Drive klasör linki verilmiş ama direkt görüntü için kullanılamaz
        // Kullanıcının admin panelden dosya görüntü linklerini eklemesi gerekiyor
        // İsteğe bağlı: Otomatik eklemek için aşağıdaki satırı kullanabilirsiniz
        // const googleDriveImageLink = 'https://drive.google.com/uc?export=view&id=FILE_ID';
        // await HomepageSettingsService.updateHeroSlides([googleDriveImageLink]);
      }
    } catch (error) {
      console.error('Error initializing settings:', error);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await HomepageSettingsService.getHomepageSettings();
      if (result.success && result.data) {
        setSettings(result.data);
        setSelectedCollections(result.data.collections || []);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ text: 'Ayarlar yüklenirken hata oluştu', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    try {
      const result = await CollectionsService.getCollections();
      if (result.success && result.data) {
        setAvailableCollections(result.data);
      }
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  const convertGoogleDriveUrl = (url: string): string => {
    // Google Drive paylaşım linkini doğru formata dönüştür
    // Birden fazla formatı destekliyoruz
    const driveRegex = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
    const match = url.match(driveRegex);
    
    if (match && match[1]) {
      const fileId = match[1];
      // Thumbnail format kullan (CORS engeli olmayan alternatif)
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1920`;
    }
    
    // Eğer zaten thumbnail veya uc formatındaysa olduğu gibi döndür
    if (url.includes('thumbnail') || url.includes('uc?export=view')) {
      return url;
    }
    
    return url;
  };

  const handleAddHeroImage = () => {
    if (!newHeroImageUrl.trim()) {
      setMessage({ text: 'Lütfen geçerli bir resim linki girin', type: 'error' });
      return;
    }

    // Google Drive linkini dönüştür
    const convertedUrl = convertGoogleDriveUrl(newHeroImageUrl.trim());

    if (!settings) {
      const newSettings: HomepageSettings = {
        heroSlides: [convertedUrl],
        collections: [],
        updatedAt: new Date()
      };
      setSettings(newSettings);
    } else {
      setSettings({
        ...settings,
        heroSlides: [...(settings.heroSlides || []), convertedUrl]
      });
    }
    setNewHeroImageUrl('');
    setMessage({ text: 'Resim eklendi. Değişiklikleri kaydetmeyi unutmayın!', type: 'success' });
  };

  const handleRemoveHeroImage = (index: number) => {
    if (!settings) return;
    const updatedSlides = [...(settings.heroSlides || [])];
    updatedSlides.splice(index, 1);
    setSettings({
      ...settings,
      heroSlides: updatedSlides
    });
    setMessage({ text: 'Resim kaldırıldı. Değişiklikleri kaydetmeyi unutmayın!', type: 'success' });
  };

  const handleMoveHeroImage = (index: number, direction: 'up' | 'down') => {
    if (!settings || !settings.heroSlides) return;
    const slides = [...settings.heroSlides];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= slides.length) return;
    
    [slides[index], slides[newIndex]] = [slides[newIndex], slides[index]];
    setSettings({
      ...settings,
      heroSlides: slides
    });
  };

  const handleToggleCollection = (collectionId: string) => {
    const index = selectedCollections.indexOf(collectionId);
    if (index > -1) {
      const updated = [...selectedCollections];
      updated.splice(index, 1);
      setSelectedCollections(updated);
    } else {
      setSelectedCollections([...selectedCollections, collectionId]);
    }
  };

  const handleMoveCollection = (index: number, direction: 'up' | 'down') => {
    const collections = [...selectedCollections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= collections.length) return;
    
    [collections[index], collections[newIndex]] = [collections[newIndex], collections[index]];
    setSelectedCollections(collections);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const updateData: Partial<HomepageSettings> = {
        heroSlides: settings?.heroSlides || [],
        collections: selectedCollections
      };
      
      const result = await HomepageSettingsService.updateHomepageSettings(updateData);
      
      if (result.success) {
        setMessage({ text: 'Ayarlar başarıyla kaydedildi!', type: 'success' });
        await loadSettings();
      } else {
        setMessage({ text: 'Ayarlar kaydedilirken hata oluştu', type: 'error' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ text: 'Ayarlar kaydedilirken hata oluştu', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Container>Yükleniyor...</Container>;
  }

  return (
    <Container>
      {message && (
        <Message $type={message.type}>
          {message.text}
        </Message>
      )}

      {/* Hero Section Management */}
      <Section>
        <SectionTitle>
          <ImageIcon size={24} />
          Hero Section (Ana Slider) Yönetimi
        </SectionTitle>
        <DescriptionText>
          Ana sayfadaki büyük slider resimlerini yönetin. Resim linklerini ekleyip çıkarabilirsiniz.
          <br /><br />
          <strong>Google Drive'dan resim eklemek için:</strong>
          <br />
          Google Drive paylaşım linkini direkt olarak yapıştırabilirsiniz. Sistem otomatik olarak thumbnail formatına çevirecektir.
          <br />
          Örnek: <code>https://drive.google.com/file/d/1AbCdEfGhIjKlMn/view</code>
        </DescriptionText>

        <AddImageContainer>
          <Input
            type="text"
            placeholder="Resim linkini buraya yapıştırın..."
            value={newHeroImageUrl}
            onChange={(e) => setNewHeroImageUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddHeroImage()}
          />
          <Button $variant="primary" onClick={handleAddHeroImage}>
            <Plus size={18} />
            Resim Ekle
          </Button>
        </AddImageContainer>

        <ImageList>
          {settings?.heroSlides?.map((url, index) => (
            <ImageItem key={index}>
              <ImagePreview $bgImage={url} />
              <ImageUrl>{url}</ImageUrl>
              <ButtonGroup>
                <Button
                  onClick={() => handleMoveHeroImage(index, 'up')}
                  disabled={index === 0}
                  title="Yukarı taşı"
                >
                  <ArrowUp size={16} />
                </Button>
                <Button
                  onClick={() => handleMoveHeroImage(index, 'down')}
                  disabled={index === (settings.heroSlides?.length || 0) - 1}
                  title="Aşağı taşı"
                >
                  <ArrowDown size={16} />
                </Button>
                <Button
                  $variant="danger"
                  onClick={() => handleRemoveHeroImage(index)}
                >
                  <Trash2 size={16} />
                  Kaldır
                </Button>
              </ButtonGroup>
            </ImageItem>
          ))}
          {(!settings?.heroSlides || settings.heroSlides.length === 0) && (
            <EmptyMessage>
              Henüz resim eklenmedi
            </EmptyMessage>
          )}
        </ImageList>
      </Section>

      {/* Collections Order Management */}
      <Section>
        <SectionTitle>
          <ImageIcon size={24} />
          Koleksiyonlar Sıralaması
        </SectionTitle>
        <DescriptionText>
          Ana sayfada gösterilecek koleksiyonları seçin ve sıralayın.
        </DescriptionText>

        <CollectionsList>
          {availableCollections.map((collection) => {
            const isSelected = selectedCollections.includes(collection.id || '');
            const selectedIndex = isSelected ? selectedCollections.indexOf(collection.id || '') : -1;
            
            return (
              <CollectionItem key={collection.id}>
                <CheckboxInput
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleCollection(collection.id || '')}
                  aria-label={`${collection.title} koleksiyonunu seç`}
                />
                <CollectionInfo>
                  <CollectionTitle>{collection.title}</CollectionTitle>
                  <CollectionDescription>{collection.description}</CollectionDescription>
                </CollectionInfo>
                {isSelected && (
                  <ButtonGroup>
                    <Button
                      onClick={() => handleMoveCollection(selectedIndex, 'up')}
                      disabled={selectedIndex === 0}
                      title="Yukarı taşı"
                    >
                      <ArrowUp size={16} />
                    </Button>
                    <Button
                      onClick={() => handleMoveCollection(selectedIndex, 'down')}
                      disabled={selectedIndex === selectedCollections.length - 1}
                      title="Aşağı taşı"
                    >
                      <ArrowDown size={16} />
                    </Button>
                  </ButtonGroup>
                )}
              </CollectionItem>
            );
          })}
          {availableCollections.length === 0 && (
            <EmptyMessage>
              Henüz koleksiyon eklenmedi. Önce "Koleksiyon Yönetimi" bölümünden koleksiyon ekleyin.
            </EmptyMessage>
          )}
        </CollectionsList>
      </Section>

      <Button $variant="primary" onClick={handleSave} disabled={saving}>
        <Save size={18} />
        {saving ? 'Kaydediliyor...' : 'Tüm Değişiklikleri Kaydet'}
      </Button>
    </Container>
  );
};

export default HomepageManagement;

